/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useRef, useCallback } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { motion, AnimatePresence } from "framer-motion";
import { useStore } from "@tanstack/react-store";
import { Menu, X, ChevronDown, Phone, Mail, ArrowRight } from "lucide-react";
import { appStore, toggleMobileMenu, closeMobileMenu } from "@/store";
import { cn } from "@/lib/utils";
import { navLinks, serviceLinks, insightsLinks } from "@/lib/constants";
import { Logo } from "../common/Logo";

const CONTAINER = "max-w-7xl mx-auto px-6";
const NAV_HEIGHT = "h-16";
const CLOSE_DELAY = 150;
const DRAWER_EXIT_DURATION_MS = 350;
const EASE_OUT_QUINT = [0.22, 1, 0.36, 1] as const;

const PHONE_NUMBER = "+254 703 173405";
const PHONE_HREF = "tel:+254703173405";
const EMAIL_ADDRESS = "info@rosewoodconsulting.co.ke";
const EMAIL_HREF = "mailto:info@rosewoodconsulting.co.ke";

type NavLink = { label: string; href: string };

function useHoverDropdown() {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const requestOpen = useCallback(
    (next: boolean) => {
      cancelClose();
      setOpen(next);
    },
    [cancelClose]
  );

  const handleEnter = useCallback(() => requestOpen(true), [requestOpen]);

  const handleLeave = useCallback(() => {
    cancelClose();
    closeTimer.current = setTimeout(() => requestOpen(false), CLOSE_DELAY);
  }, [cancelClose, requestOpen]);

  useEffect(() => cancelClose, [cancelClose]);

  return { open, requestOpen, handleEnter, handleLeave };
}

function ContactLinks({
  variant,
  onLinkClick,
}: {
  variant: "topbar" | "drawer";
  onLinkClick?: () => void;
}) {
  const isTopbar = variant === "topbar";

  return (
    <div
      className={cn(
        isTopbar
          ? "flex items-center gap-6"
          : "flex items-center justify-center gap-5 text-sm text-neutral-800"
      )}
    >
      
      <a  href={PHONE_HREF}
        onClick={onLinkClick}
        className={cn(
          "flex items-center gap-1.5 transition-colors",
          isTopbar ? "hover:text-white" : "hover:text-burgundy"
        )}
      >
        <Phone size={isTopbar ? 11 : 13} />
        <span>{PHONE_NUMBER}</span>
      </a>
      
       <a href={EMAIL_HREF}
        onClick={onLinkClick}
        className={cn(
          "flex items-center gap-1.5 transition-colors",
          isTopbar ? "hover:text-white" : "hover:text-burgundy"
        )}
      >
        <Mail size={isTopbar ? 11 : 13} />
        <span>{isTopbar ? EMAIL_ADDRESS : "Email us"}</span>
      </a>
    </div>
  );
}

function MobileDrawer({ open, pathname }: { open: boolean; pathname: string }) {
  const [servicesExpanded, setServicesExpanded] = useState(false);
  const [insightsExpanded, setInsightsExpanded] = useState(false);

  useEffect(() => {
    if (open) return;
    const t = setTimeout(() => {
      setServicesExpanded(false);
      setInsightsExpanded(false);
    }, DRAWER_EXIT_DURATION_MS);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm lg:hidden"
            onClick={closeMobileMenu}
            aria-hidden="true"
          />

          <motion.div
            key="panel"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 320, damping: 32, mass: 0.9 }}
            className="fixed top-0 right-0 bottom-0 z-50 w-[min(88vw,360px)] bg-white flex flex-col lg:hidden"
            role="dialog"
            aria-modal="true"
            aria-label="Navigation menu"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E8E0D0]">
              <Logo size="md" imageSize="sm" onClick={closeMobileMenu} />
              <button
                onClick={closeMobileMenu}
                className="p-2 rounded-lg text-neutral-800 hover:text-[#1A1A1A] hover:bg-[#F5F5F5] transition-colors"
                aria-label="Close menu"
              >
                <X size={20} />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto overscroll-contain py-3 px-3">
              {navLinks.map((link) => {
                const isActive =
                  link.href === "/" ? pathname === "/" : pathname.startsWith(link.href);

                if (!link.hasDropdown) {
                  return (
                    <Link
                      key={link.href}
                      to={link.href as never}
                      onClick={closeMobileMenu}
                      className={cn(
                        "flex items-center px-3 py-3 rounded-xl text-[15px] font-medium transition-colors",
                        isActive
                          ? "text-burgundy bg-burgundy/6"
                          : "text-[#1A1A1A] hover:text-burgundy hover:bg-burgundy/5"
                      )}
                    >
                      {link.label}
                      {isActive && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-burgundy" />}
                    </Link>
                  );
                }

                const isServices = link.label === "Services";
                const expanded = isServices ? servicesExpanded : insightsExpanded;
                const setExpanded = isServices ? setServicesExpanded : setInsightsExpanded;
                const linksList: NavLink[] = isServices ? serviceLinks : insightsLinks;
                const viewAll: NavLink | undefined = isServices
                  ? { label: "View All Services", href: "/services" }
                  : undefined;

                return (
                  <div key={link.href}>
                    <button
                      onClick={() => setExpanded((v) => !v)}
                      className={cn(
                        "w-full flex items-center px-3 py-3 rounded-xl text-[15px] font-medium transition-colors",
                        (isServices && pathname.startsWith("/services")) ||
                          (!isServices && pathname.startsWith("/insights"))
                          ? "text-burgundy bg-burgundy/6"
                          : "text-[#1A1A1A] hover:text-burgundy hover:bg-burgundy/5"
                      )}
                      aria-expanded={expanded}
                    >
                      {link.label}
                      <ChevronDown
                        size={16}
                        className={cn(
                          "ml-auto transition-transform duration-200",
                          expanded && "rotate-180"
                        )}
                      />
                    </button>

                    <AnimatePresence initial={false}>
                      {expanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.22, ease: EASE_OUT_QUINT }}
                          className="overflow-hidden"
                        >
                          <div className="ml-3 mt-0.5 mb-1 pl-3 border-l-2 border-[#E8E0D0] flex flex-col gap-0.5">
                            {linksList.map((item) => (
                              <Link
                                key={item.href}
                                to={item.href as never}
                                onClick={closeMobileMenu}
                                className={cn(
                                  "flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-colors group",
                                  pathname === item.href
                                    ? "text-burgundy font-medium bg-burgundy/5"
                                    : "text-[#4A4A4A] hover:text-burgundy hover:bg-burgundy/5"
                                )}
                              >
                                {item.label}
                                <ArrowRight
                                  size={13}
                                  className="opacity-0 group-hover:opacity-100 transition-opacity text-burgundy"
                                />
                              </Link>
                            ))}
                            {viewAll && (
                              <Link
                                to={viewAll.href as never}
                                onClick={closeMobileMenu}
                                className="flex items-center gap-1.5 px-3 py-2.5 mt-1 rounded-lg text-sm font-semibold text-burgundy hover:bg-burgundy/5 transition-colors"
                              >
                                {viewAll.label}
                                <ArrowRight size={13} />
                              </Link>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </nav>

            <div className="px-4 py-5 border-t border-[#E8E0D0] flex flex-col gap-3 bg-cream">
              <Link
                to="/contact"
                onClick={closeMobileMenu}
                className="flex items-center justify-center gap-2 w-full py-3 text-sm font-semibold text-white bg-burgundy rounded-xl hover:bg-[#4A1219] active:scale-[0.98] transition-all"
              >
                Contact Us
                <ArrowRight size={15} />
              </Link>
              <ContactLinks variant="drawer" />
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function DesktopDropdown({
  label,
  links,
  viewAllLink,
  isActive,
}: {
  label: string;
  links: NavLink[];
  viewAllLink?: NavLink;
  isActive: boolean;
}) {
  const { open, handleEnter, handleLeave } = useHoverDropdown();

  return (
    <div
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      className="relative"
    >
      <button
        onFocus={handleEnter}
        aria-expanded={open}
        className={cn(
          "flex items-center gap-1 px-4 py-2 font-medium rounded-md transition-all",
          isActive
            ? "text-burgundy bg-burgundy/5"
            : "text-[#1A1A1A] hover:text-burgundy hover:bg-burgundy/5"
        )}
      >
        {label}
        <motion.span
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.2, ease: EASE_OUT_QUINT }}
          className="flex"
        >
          <ChevronDown size={14} />
        </motion.span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.16, ease: EASE_OUT_QUINT }}
            className="absolute left-1/2 top-full -translate-x-1/2 pt-3 w-[280px] z-50"
          >
            <div className="rounded-xl bg-white p-2 ring-1 ring-black/[0.06]">
              {links.map((item, i) => (
                <motion.div
                  key={item.href}
                  initial={{ opacity: 0, x: -4 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.14, delay: 0.02 * i, ease: EASE_OUT_QUINT }}
                >
                  <Link
                    to={item.href as never}
                    onClick={handleLeave}
                    className="flex w-full cursor-pointer rounded-lg px-2 py-2 text-sm transition-colors focus:bg-accent focus:text-accent-foreground hover:bg-accent hover:text-accent-foreground"
                  >
                    {item.label}
                  </Link>
                </motion.div>
              ))}
              {viewAllLink && (
                <>
                  <div className="-mx-1 my-1 h-px bg-border" />
                  <Link
                    to={viewAllLink.href as never}
                    onClick={handleLeave}
                    className="flex w-full cursor-pointer items-center justify-center rounded-lg px-2 py-2 text-sm font-semibold text-burgundy hover:bg-accent"
                  >
                    {viewAllLink.label} →
                  </Link>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const isMobileMenuOpen = useStore(appStore, (s) => s.isMobileMenuOpen);
  const routerState = useRouterState();
  const pathname = routerState.location.pathname;

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 20);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    closeMobileMenu();
  }, [pathname]);

  return (
    <>
      <div className="bg-gold text-white text-sm py-2 hidden md:block">
        <div className={CONTAINER}>
          <div className="flex items-center justify-between">
            <ContactLinks variant="topbar" />
            <span>Mon – Fri: 8:00 am – 5:30 pm</span>
          </div>
        </div>
      </div>

      <header
        className={cn(
          "sticky top-0 z-50 w-full transition-all duration-300",
          scrolled ? "bg-white/98 backdrop-blur-md" : "bg-white"
        )}
      >
        <div className={CONTAINER}>
          <div className={cn("flex items-center justify-between", NAV_HEIGHT)}>
            <Logo size="sm" imageSize="md" />

            <nav className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => {
                if (!link.hasDropdown) {
                  return (
                    <Link
                      key={link.href}
                      to={link.href as never}
                      className={cn(
                        "px-4 py-2 font-medium rounded-md transition-all",
                        pathname === link.href
                          ? "text-burgundy bg-burgundy/5"
                          : "text-[#1A1A1A] hover:text-burgundy hover:bg-burgundy/5"
                      )}
                    >
                      {link.label}
                    </Link>
                  );
                }

                const isServices = link.label === "Services";
                const dropdownLinks = isServices ? serviceLinks : insightsLinks;
                const viewAll: NavLink | undefined = isServices
                  ? { label: "View All Services", href: "/services" }
                  : undefined;

                const isActive =
                  (isServices && pathname.startsWith("/services")) ||
                  (!isServices && pathname.startsWith("/insights"));

                return (
                  <DesktopDropdown
                    key={link.href}
                    label={link.label}
                    links={dropdownLinks}
                    viewAllLink={viewAll}
                    isActive={isActive}
                  />
                );
              })}
            </nav>

            <div className="hidden lg:flex items-center gap-3">
              <Link
                to="/contact"
                className="px-5 py-2.5 font-semibold text-white bg-burgundy rounded-lg hover:bg-[#4A1219] transition-all active:scale-95"
              >
                Contact Us
              </Link>
            </div>

            <button
              onClick={toggleMobileMenu}
              className={cn(
                "lg:hidden p-2 rounded-lg transition-colors",
                isMobileMenuOpen
                  ? "text-burgundy bg-burgundy/8"
                  : "text-[#1A1A1A] hover:bg-burgundy/5"
              )}
              aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-nav"
            >
              <motion.div
                animate={{ rotate: isMobileMenuOpen ? 90 : 0 }}
                transition={{ duration: 0.2 }}
              >
                {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
              </motion.div>
            </button>
          </div>
        </div>
      </header>

      <MobileDrawer open={isMobileMenuOpen} pathname={pathname} />
    </>
  );
}